/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect } from 'react';
import joi from 'joi';
import formErrors from 'constants/formErrors';
import { Button, DynamicForm } from 'components';
import { Props } from 'types/modals';
import useFormHandler from 'hooks/useFormHandler';
import Base from '../../base';
import schema, { triggers } from './schema';

const CreationNotificationModal = ({ closing, onClose }: Props) => {
  const formHandler = useFormHandler(schema);

  const handleSubmit = () => {
    const { data, schema } = formHandler;
    const formData = { ...data };

    if (schema.account.hide) {
      delete formData.account;
    }
    if (schema.venue.hide) {
      delete formData.venue;
    }
    if (schema.event?.hide) {
      delete formData.event;
    }
    onClose(formData);
  };

  useEffect(() => {
    const { data, setFullSchema } = formHandler;

    switch (data.trigger) {
      case triggers.ALL_EMPLOYEES:
        setFullSchema(
          {
            ...schema,
            account: {
              ...schema.account,
              hide: true,
              joi: joi.allow('').optional()
            },
            venue: {
              ...schema.venue,
              hide: true,
              joi: joi.allow('').optional()
            },
            event: {
              ...schema.event,
              hide: true,
              joi: joi.allow('').optional()
            },
            isSMS: {
              ...schema.isSMS,
              hide: false
            }
          },
          {
            ...data,
            account: '',
            venue: '',
            event: ''
          }
        );
        break;
      case triggers.ONLY_EMPLOYEES_FROM_ACCOUNT:
        setFullSchema(
          {
            ...schema,
            account: {
              ...schema.account,
              hide: false
            },
            venue: {
              ...schema.venue,
              hide: true,
              joi: joi.allow('').optional()
            },
            event: {
              ...schema.event,
              hide: true,
              joi: joi.allow('').optional()
            },
            isSMS: {
              ...schema.isSMS,
              hide: false
            }
          },
          {
            ...data,
            venue: '',
            event: ''
          }
        );
        break;
      case triggers.ONLY_EMPLOYEES_FROM_VENUE:
        setFullSchema(
          {
            ...schema,
            account: {
              ...schema.account,
              hide: true,
              joi: joi.allow('').optional()
            },
            venue: {
              ...schema.venue,
              hide: false
            },
            event: {
              ...schema.event,
              hide: true,
              joi: joi.allow('').optional()
            },
            isSMS: {
              ...schema.isSMS,
              hide: false
            }
          },
          {
            ...data,
            account: '',
            event: ''
          }
        );
        break;
      case triggers.ONLY_EMPLOYEES_FROM_EVENT:
        setFullSchema(
          {
            ...schema,
            account: {
              ...schema.account,
              hide: true,
              joi: joi.allow('').optional()
            },
            venue: {
              ...schema.venue,
              hide: true,
              joi: joi.allow('').optional()
            },
            event: {
              ...schema.event,
              hide: false,
              joi: joi.string().required().messages({ 'string.empty': formErrors.REQUIRED })
            },
            isSMS: {
              ...schema.isSMS,
              hide: false
            }
          },
          {
            ...data,
            account: '',
            venue: ''
          }
        );
        break;
      default:
        break;
    }
  }, [formHandler.data.trigger]);

  return (
    <Base
      header="Create Notification"
      closing={closing}
      onClose={onClose}
      size="md"
      footer={
        <Button
          text="Create"
          disabled={
            !formHandler.valid ||
            formHandler.isSubmitting ||
            (!formHandler.data.isSMS && !formHandler.data.isEmail && !formHandler.data.isPush)
          }
          onClick={handleSubmit}
        />
      }
    >
      <DynamicForm
        handler={{
          ...formHandler,
          schema: Object.fromEntries(
            Object.entries(formHandler.schema).filter(
              ([key]) => key !== 'isSMS' && key !== 'isEmail' && key !== 'isPush'
            )
          )
        }}
      />
      <div style={{ display: 'flex', gap: '2rem', marginTop: '0.5rem', marginBottom: '0.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            id="isSMS"
            checked={formHandler.data.isSMS}
            onChange={e => formHandler.setFormData('isSMS')(e.target.checked)}
            disabled={formHandler.schema.isSMS?.disabled}
          />
          <label htmlFor="isSMS" style={{ marginLeft: 8, fontWeight: 500 }}>
            {formHandler.schema.isSMS?.label}
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            id="isEmail"
            checked={formHandler.data.isEmail}
            onChange={e => formHandler.setFormData('isEmail')(e.target.checked)}
            disabled={formHandler.schema.isEmail?.disabled}
          />
          <label htmlFor="isEmail" style={{ marginLeft: 8, fontWeight: 500 }}>
            {formHandler.schema.isEmail?.label}
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            id="isPush"
            checked={formHandler.data.isPush}
            onChange={e => formHandler.setFormData('isPush')(e.target.checked)}
            disabled={formHandler.schema.isPush?.disabled}
          />
          <label htmlFor="isPush" style={{ marginLeft: 8, fontWeight: 500 }}>
            {formHandler.schema.isPush?.label}
          </label>
        </div>
      </div>
    </Base>
  );
};

export default CreationNotificationModal;
